import React, { Component } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';
import { authService } from '../../services/authService';

import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  Grid, 
  Row, 
  Col,
  Input,
  Select,
  Divider,
  Spin,
  Tooltip,
  Popconfirm,
  message,
} from 'antd';

import {
  FileZipOutlined,
  FileDoneOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons';

import Addon from './Addon';
import { API_BASE_URL } from '../../config/api';

const { useBreakpoint } = Grid;

class ReviewResults extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      skip: 0,
      limit: 20,
      totalCount: 0,
      items: [],
      clientCode: props.client.ClientCode,

      filters: {
        accessionID: '',
        lastName: '',
        firstName: '',
        dob: '',
        collectionDate: '',
        physician: '',
        status: '',
        flag: '',
        reviewed: '',
      },
      physicians: [],

      addonItem: {},
      addTest: false,
    };
  }

  componentDidMount = () => {
    this.loadData();
    this.loadPhysicians();
  }

  loadPhysicians = async () => {
    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      return;
    }

    let params = {
      clientCode: this.state.clientCode
    }
    axios({
      method: 'GET',
      url: `${API_BASE_URL}/connect/physicians/search`,
      params: params,
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      this.setState({
        physicians: res.data.physicians,
      })
    }).catch(err =>{
      console.log(err);
    })
  }

  handleFilterChange = (field, value) => {
    this.setState((prev) => ({
      filters: {
        ...prev.filters,
        [field]: value,
      },
    }));
  };

  clearFilters = () => {
    this.setState({
      filters: {
        accessionID: '',
        lastName: '',
        firstName: '',
        dob: '',
        collectionDate: '',
        physician: '',
        status: '',
        flag: '',
        reviewed: '',
      },
    });
  };

  onAddTest = (item) => {
    this.setState({
      addTest: !this.state.addTest,
      addonItem: item,
    })
  }

  // Mark an accession reviewed for the whole practice (per-client scope). Runs as a
  // silent background action when a report is opened — see viewReport. Failing
  // quietly is safe: the row simply stays "needs review" (bold) until next open.
  markReviewed = async (accession) => {
    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      return;
    }
    const u = authService.getUserAttributes() || {};
    const reviewedBy = u.name || u.preferred_username || u.email || '';
    try {
      const res = await axios({
        method: 'POST',
        url: `${API_BASE_URL}/connect/accessions/${accession}/review`,
        data: { clientCode: this.state.clientCode, reviewedBy },
        headers: { Authorization: `Bearer ${token}` },
      });
      const review = res.data.review || {};
      this.setState((prev) => ({
        items: prev.items.map((it) =>
          it.accession === accession
            ? {
                ...it,
                reviewed: true,
                updatedSinceReview: false,
                reviewedBy: review.reviewedBy ?? reviewedBy,
                reviewedAt: review.reviewedAt ?? new Date().toISOString(),
              }
            : it
        ),
      }));
    } catch (err) {
      console.log(err);
    }
  }

  // Undo a review for the whole practice — removes the review record so the
  // accession reverts to needs-review. Updates the row in place.
  markUnreviewed = async (accession) => {
    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      return;
    }
    try {
      await axios({
        method: 'DELETE',
        url: `${API_BASE_URL}/connect/accessions/${accession}/review`,
        data: { clientCode: this.state.clientCode },
        headers: { Authorization: `Bearer ${token}` },
      });
      this.setState((prev) => ({
        items: prev.items.map((it) =>
          it.accession === accession
            ? { ...it, reviewed: false, updatedSinceReview: false, reviewedBy: null, reviewedAt: null }
            : it
        ),
      }));
    } catch (err) {
      console.log(err);
    }
  }

  // Clean slate: mark every currently-unreviewed accession for the practice as
  // reviewed in one shot, then reload so the list reflects it.
  reviewAll = async () => {
    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      return;
    }
    const u = authService.getUserAttributes() || {};
    const reviewedBy = u.name || u.preferred_username || u.email || '';
    try {
      const res = await axios({
        method: 'POST',
        url: `${API_BASE_URL}/connect/accessions/review-all`,
        data: { clientCode: this.state.clientCode, reviewedBy },
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success(`Marked ${res.data.marked} result(s) as reviewed`);
      this.setState({ skip: 0 }, () => this.loadData());
    } catch (err) {
      console.log(err);
      message.error('Failed to review all');
    }
  }

  loadData = async () => {
    this.setState({loading: true})
    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      return;
    }
    const params = {
      clientCode: this.state.clientCode,
      skip: this.state.skip,
      limit: this.state.limit,
      accessionID: this.state.filters.accessionID || undefined,
      lastName: this.state.filters.lastName || undefined,
      firstName: this.state.filters.firstName || undefined,
      dob: this.state.filters.dob || undefined,
      physicianCode: this.state.filters.physician || undefined,
      collectionDate: this.state.filters.collectionDate || undefined,
      flag: this.state.filters.flag || undefined,
      status: this.state.filters.status || undefined,
      reviewed: this.state.filters.reviewed || undefined,
    }
    axios({
      method: 'GET',
      url: `${API_BASE_URL}/connect/accessions`,
      params: params,
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      this.setState({
        items: res.data.items,
        totalCount: res.data.total,
        loading: false,
      })
    }).catch(err =>{
      console.log(err);
    })
  }

  // Fetch the report PDF WITH the auth header, then open it as a blob. A plain
  // <a href> can't do this — the browser navigates without the Bearer token, so
  // the now-protected endpoint would return 401. The blank tab is opened
  // synchronously inside the click so the popup blocker allows it.
  viewReport = async (accession) => {
    const win = window.open('', '_blank');
    try {
      const token = await authService.ensureValidToken();
      if (!token) {
        console.error('Not authenticated');
        if (win) win.close();
        return;
      }
      const res = await axios({
        method: 'GET',
        url: `${API_BASE_URL}/connect/accessions/report/${accession}`,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (win) win.location = blobUrl;
      else window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      // Opening the report counts as the practice reviewing it.
      this.markReviewed(accession);
    } catch (err) {
      if (win) win.close();
      console.log(err);
    }
  }

  render() {
    const { screens } = this.props;
    const isSmall = !screens?.lg;

    const shortFlag = (flag) => {
      if (!flag) return '';
      if (!isSmall) return flag;
      if (flag === 'Abnormal') return 'ABN';
      if (flag === 'Critical') return 'CRT';
      return flag[0];
    };

    const shortStatus = (status) => {
      if (!status) return '';
      if (!isSmall) return status;
      if (status === 'Preliminary') return 'PRE';
      if (status === 'Pending') return 'PND';
      if (status === 'Final') return 'FIN';
      return status;
    };

    const columns = [
      {
        // Outlook-style unread bar: orange = unreviewed, faint grey = reviewed.
        // Clicking it toggles the review state for the practice.
        title: '',
        key: 'reviewToggle',
        width: 22,
        onCell: () => ({ style: { position: 'relative', padding: 0 } }),
        render: (_, record) => {
          // Pre-cutoff rows are reviewed with no real record — nothing to toggle.
          if (record.reviewed && !record.reviewedAt) return null;
          return (
            <Tooltip placement="right" title={record.reviewed ? 'Mark unreviewed' : 'Mark reviewed'}>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  if (record.reviewed) this.markUnreviewed(record.accession);
                  else this.markReviewed(record.accession);
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: 22,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    width: 4,
                    height: '55%',
                    borderRadius: 2,
                    backgroundColor: record.reviewed ? '#d9d9d9' : '#fa8c16',
                  }}
                />
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: 'Accession',
        dataIndex: 'accession',
        key: 'accession',
        responsive: ['md'],
      },
       {
        title: 'Patient Name',
        key: 'patientName',
        render: (_, record) => {
          const p = record.patient || {};
          // "LAST, First M." format
          const mi = p.middleName ? ` ${p.middleName[0]}.` : '';
          const name = `${p.lastName || ''}, ${p.firstName || ''}${mi}`.trim().replace(/^, /, '');
          return (
            <div>
              <div>{name}</div>
              {/* The Accession column is hidden below md, so surface the number
                  here on small screens where it's otherwise impossible to see. */}
              {!screens?.md && (
                <div style={{ color: '#888', fontSize: 12, lineHeight: 1.2 }}>
                  {record.accession}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: 'DOB',
        dataIndex: ['patient', 'dob'],
        key: 'dob',
        responsive: ['md'],
        render: (value) => value ? moment(value, 'YYYY-MM-DD').format('MM/DD/YYYY') : '',
      },
      {
        title: 'Physician',
        key: 'physicianName',
        responsive: ['lg'],
        render: (_, record) => {
          const d = record.physician || {};
          // "LAST, First" or "First Last" – choose your UI preference
          return `${d.lastName || ''}, ${d.firstName || ''}`.trim().replace(/^, /, '');
        },
      },
      {
        title: 'Collected',
        dataIndex: 'collectionDate',
        key: 'collectionDate',
        responsive: ['md'],
        render: (value) => moment(value, 'YYYY-MM-DD HH:mm:ss').format("MM/DD/YYYY"),
      },
      {
        title: 'Flag',
        dataIndex: 'flag',
        key: 'flag',
        render: (_, record) => {
          if (!record.flag) return null;

          const label = shortFlag(record.flag);

          return (
            <Tag
              color={record.flag === 'Abnormal' ? 'volcano' : 'red'}
              style={isSmall ? { padding: '0 6px' } : undefined}
            >
              {label}
            </Tag>
          );
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (_, record) => {
          const label = shortStatus(record.status);

          return (
            <Tag
              color={
                record.status === 'Final'
                  ? 'blue'
                  : record.status === 'Pending'
                  ? 'orange'
                  : 'default'
              }
              style={isSmall ? { padding: '0 6px' } : undefined}
            >
              {label}
            </Tag>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => {
          const who = record.reviewedBy ? ` by ${record.reviewedBy}` : '';
          const when = record.reviewedAt ? ` on ${moment(record.reviewedAt).format('MM/DD/YYYY')}` : '';
          // The report button doubles as the reviewed indicator: a closed/zipped
          // file means "not opened yet", an opened "done" document means reviewed.
          const reportTip = record.reviewed
            ? `View report — reviewed${who}${when}`
            : record.updatedSinceReview
            ? `View report — new results since review${who}${when}`
            : 'View report — not yet reviewed';
          return (
          <Space onClick={(e) => e.stopPropagation()}>
            <Tooltip placement="top" title={reportTip}>
              <Button
                size="small"
                type={record.reviewed ? 'default' : 'primary'}
                onClick={() => this.viewReport(record.accession)}
                style={record.reviewed ? undefined : { backgroundColor: '#fa8c16', borderColor: '#fa8c16' }}
              >
                {record.reviewed ? <FileDoneOutlined /> : <FileZipOutlined />}
              </Button>
            </Tooltip>

            <Tooltip placement="top" title="Add test">
              {moment().diff(moment(record.collectionDate), 'days') <= 7 && (
                <Button size="small" type="primary" onClick={() => this.onAddTest(record)}>
                  <PlusCircleOutlined />
                </Button>
              )}
            </Tooltip>

          </Space>
          );
        },
      },
    ];

    return (
      <Row>
        <Col span={24}>
          <Space wrap style={{ width: '100%' }}>
            <Input
              placeholder="Accession"
              allowClear
              value={this.state.filters.accessionID}
              onChange={(e) => this.handleFilterChange('accessionID', e.target.value)}
              style={{ width: 150 }}
            />

            <Input
              placeholder="Last Name"
              allowClear
              value={this.state.filters.lastName}
              onChange={(e) => this.handleFilterChange('lastName', e.target.value)}
              style={{ width: 150 }}
            />

            <Input
              placeholder="Fist Name"
              allowClear
              value={this.state.filters.firstName}
              onChange={(e) => this.handleFilterChange('firstName', e.target.value)}
              style={{ width: 150 }}
            />

            <Input
              placeholder="DOB"
              allowClear
              value={this.state.filters.dob}
              onChange={(e) => this.handleFilterChange('dob', e.target.value)}
              style={{ width: 150 }}
            />

            <Select
              placeholder="Physician"
              allowClear
              value={this.state.filters.physician || undefined}
              onChange={(value) => this.handleFilterChange('physician', value)}
              style={{ width: 180 }}
            >
              {this.state.physicians.map(item => 
                <Select.Option key={item.id} value={item.PhysicianCode}>{item.LastName}, {item.FirstName}</Select.Option>
              )}
            </Select>

            <Input
              placeholder="Collected"
              allowClear
              value={this.state.filters.collectionDate}
              onChange={(e) => this.handleFilterChange('collectionDate', e.target.value)}
              style={{ width: 150 }}
            />

            <Select
              placeholder="Flag"
              allowClear
              value={this.state.filters.flag || undefined}
              onChange={(value) => this.handleFilterChange('flag', value)}
              style={{ width: 140 }}
            >
              <Select.Option value="Abnormal">Abnormal</Select.Option>
              <Select.Option value="Critical">Critical</Select.Option>
            </Select>

            <Select
              placeholder="Status"
              allowClear
              value={this.state.filters.status || undefined}
              onChange={(value) => this.handleFilterChange('status', value)}
              style={{ width: 140 }}
            >
              <Select.Option value="Final">Final</Select.Option>
              <Select.Option value="Pending">Pending</Select.Option>
              <Select.Option value="Preliminary">Preliminary</Select.Option>
            </Select>

            <Select
              placeholder="Reviewed"
              allowClear
              value={this.state.filters.reviewed || undefined}
              onChange={(value) => this.handleFilterChange('reviewed', value)}
              style={{ width: 140 }}
            >
              <Select.Option value="unreviewed">Unreviewed</Select.Option>
              <Select.Option value="reviewed">Reviewed</Select.Option>
            </Select>

            <Button type="primary"onClick={() => {
              this.setState({ skip: 0 }, () => this.loadData());
            }}><SearchOutlined /> Search</Button>
            <Button onClick={this.clearFilters}>Clear</Button>

            <Popconfirm
              title="Mark all unreviewed results as reviewed?"
              description="This clears the entire unreviewed queue for your practice. Only new results will show as unreviewed afterward."
              okText="Review all"
              cancelText="Cancel"
              onConfirm={this.reviewAll}
            >
              <Button icon={<CheckSquareOutlined />}>Review All</Button>
            </Popconfirm>
          </Space>
        </Col>
        <Divider/>
        <Col span={24}>

          {this.state.loading ?
            <div style={{textAlign:"center"}}>
              <Spin size="large"/>
            </div>          
            :
            <Table
              dataSource={this.state.items}
              columns={columns}
              pagination={{
                current: Math.floor(this.state.skip / this.state.limit) + 1,
                pageSize: this.state.limit,
                total: this.state.totalCount,
                showSizeChanger: true,
                pageSizeOptions: ['10', '25', '50', '100'],
                onChange: (page) => {
                  this.setState(
                    { skip: (page - 1) * this.state.limit },
                    () => this.loadData()
                  );
                },
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
              size="small"
              rowKey="id"
              rowClassName={(record) =>
                [
                  record.flag === 'Critical' ? 'row-critical' : '',
                  record.reviewed ? '' : 'row-unreviewed',
                ].join(' ').trim()
              }
              onRow={(record) => ({
                onClick: () => this.props.navigate(`/results/${record.accession}`),
                style: { cursor: 'pointer' },
              })}              
            />
          }
          {this.state.addTest && (
            <Addon
              visible={this.state.addTest}
              onClose={() => this.setState({ addTest: false })}
              addonItem={this.state.addonItem}
            />
          )}
        </Col>
      </Row>      
    );
  }
}
export default function ReviewResultsWithBreakpoints(props) {
  const screens = useBreakpoint();
  const navigate = useNavigate();
  return <ReviewResults {...props} screens={screens} navigate={navigate}/>;
}
